import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Download, CheckCircle, AlertTriangle, ExternalLink } from "lucide-react";
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { getTelemetry } from "../../lib/api";
import { formatDate } from "../../lib/utils";

const PIE_COLORS = ["#10b981", "#ef4444"];

export default function TelemetryPage() {
  const navigate = useNavigate();
  const [telemetry, setTelemetry] = useState([]);
  const [filter, setFilter] = useState("");
  const [outcomeFilter, setOutcomeFilter] = useState("ALL");

  useEffect(() => {
    getTelemetry().then(setTelemetry).catch(console.error);
  }, []);

  const successCount = telemetry.filter((t) => t.outcome === "SUCCESS").length;
  const rollbackCount = telemetry.filter((t) => t.outcome === "ROLLBACK").length;
  const pieData = [
    { name: "SUCCESS", value: successCount },
    { name: "ROLLBACK", value: rollbackCount },
  ];

  // Error code bar chart
  const errorCodes = telemetry
    .filter((t) => t.errorCode)
    .reduce((acc, t) => {
      acc[t.errorCode] = (acc[t.errorCode] || 0) + 1;
      return acc;
    }, {});
  const barData = Object.entries(errorCodes).map(([code, count]) => ({ code, count }));

  const filtered = telemetry
    .filter((t) => outcomeFilter === "ALL" || t.outcome === outcomeFilter)
    .filter(
      (t) =>
        t.deviceSerial.toLowerCase().includes(filter.toLowerCase()) ||
        t.baselineVersion.toLowerCase().includes(filter.toLowerCase()) ||
        (t.errorCode || "").toLowerCase().includes(filter.toLowerCase())
    )
    .sort((a, b) => new Date(b.reportedAt) - new Date(a.reportedAt));

  const exportCsv = () => {
    const rows = [
      ["ID", "Device", "Version", "Outcome", "Error Code", "Session ID", "Reported At"],
      ...filtered.map((t) => [t.id, t.deviceSerial, t.baselineVersion, t.outcome, t.errorCode || "", t.sessionId, t.reportedAt]),
    ];
    const csv = rows.map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "telemetry-export.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Telemetry</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            OTA outcome events — SUCCESS / ROLLBACK + error code from Mobile POST /telemetry.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={exportCsv}>
          <Download className="mr-2 h-4 w-4" /> Export CSV
        </Button>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader><CardTitle className="text-base">Outcome Distribution</CardTitle></CardHeader>
          <CardContent>
            <div className="flex items-center gap-6">
              <ResponsiveContainer width={120} height={120}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={35} outerRadius={55} dataKey="value">
                    {pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-emerald-500" />
                  <span className="text-sm">Success: <strong>{successCount}</strong></span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-500" />
                  <span className="text-sm">Rollback: <strong>{rollbackCount}</strong></span>
                </div>
                <p className="text-xs text-slate-400">
                  {telemetry.length > 0
                    ? `${((successCount / telemetry.length) * 100).toFixed(1)}% success rate`
                    : "No data"}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Error Code Breakdown</CardTitle></CardHeader>
          <CardContent>
            {barData.length === 0 ? (
              <div className="flex items-center justify-center h-24 text-slate-400 text-sm">No errors recorded</div>
            ) : (
              <ResponsiveContainer width="100%" height={120}>
                <BarChart data={barData} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="code" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#ef4444" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Filter + table */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-3">
            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input className="pl-9" placeholder="Search serial, version, error…" value={filter} onChange={(e) => setFilter(e.target.value)} />
            </div>
            {["ALL", "SUCCESS", "ROLLBACK"].map((o) => (
              <button
                key={o}
                onClick={() => setOutcomeFilter(o)}
                className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                  outcomeFilter === o ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200 hover:border-blue-300"
                }`}
              >
                {o}
              </button>
            ))}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Device</TableHead>
                <TableHead>Version</TableHead>
                <TableHead>Outcome</TableHead>
                <TableHead>Error Code</TableHead>
                <TableHead>Session ID</TableHead>
                <TableHead>Reported At</TableHead>
                <TableHead className="text-right">Device</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-10">No telemetry records.</TableCell></TableRow>
              )}
              {filtered.map((t) => (
                <TableRow key={t.id}>
                  <TableCell className="font-mono font-medium text-sm">{t.deviceSerial}</TableCell>
                  <TableCell className="font-mono text-sm">v{t.baselineVersion}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      {t.outcome === "SUCCESS"
                        ? <CheckCircle className="h-4 w-4 text-emerald-500" />
                        : <AlertTriangle className="h-4 w-4 text-red-500" />
                      }
                      <Badge variant={t.outcome === "SUCCESS" ? "success" : "destructive"} className="text-[11px]">
                        {t.outcome}
                      </Badge>
                    </div>
                  </TableCell>
                  <TableCell>
                    {t.errorCode
                      ? <code className="text-xs bg-red-50 text-red-700 px-1.5 py-0.5 rounded">{t.errorCode}</code>
                      : <span className="text-slate-300">—</span>
                    }
                  </TableCell>
                  <TableCell>
                    <code className="text-[11px] text-slate-400 truncate block max-w-[140px]">{t.sessionId}</code>
                  </TableCell>
                  <TableCell className="text-sm text-slate-500">{formatDate(t.reportedAt)}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => navigate(`/devices/${t.deviceSerial}`)}>
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
